import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router';
import {
    Card,
    Typography,
    Alert,
    Space,
    Skeleton,
    DatePicker,
    Select,
    Button,
    Row,
    Col,
    Spin,
    Empty,
    Tooltip,
    Checkbox,
} from 'antd';
import { ErrorComponent } from "@refinedev/antd";
import {
    ReloadOutlined
} from '@ant-design/icons';
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    ReferenceArea,
    ComposedChart,
} from 'recharts';
import { useCustom } from "@refinedev/core";
import dayjs from 'dayjs';
import { ZoomableMultiLineChart } from "../../components/charts/ZoomableMultiLineChart";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface RouteParams extends Record<string, string | undefined> {
    meterID: string;
    concentratorID: string;
}

interface ChartDataPoint {
    timestamp: string;
    value?: number;
    active_value?: number;
    reactive_value?: number;
    value_kwh?: number;
    value_gj?: number;
}

interface MeterInfo {
    uuid: string;
    name: string;
    media: 'electricity' | 'water' | 'heat';
    unit?: string;
}

interface ChartResponse {
    meter: MeterInfo;
    data: ChartDataPoint[];
    query: {
        start_date: string;
        end_date: string;
        granularity: string;
        total_points: number;
    };
}

export const MeterChart: React.FC = () => {
    const { meterID, concentratorID } = useParams<RouteParams>();
    // const navigate = useNavigate();

    // Chart state
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);
    const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day');
    const [zoomState, setZoomState] = useState({
        refAreaLeft: '',
        refAreaRight: '',
    });
    const [showAllReadouts, setShowAllReadouts] = useState(false);

    // Fetch chart data
    const {
        query: {
            isLoading,
            error,
            refetch
        },

        result: chartData
    } = useCustom<ChartResponse>({
        url: `/api/concentrators/${concentratorID}/meters/${meterID}/chart-data/`,
        method: 'get',
        config: {
            query: {
                start_date: dateRange[0].toISOString(),
                end_date: dateRange[1].toISOString(),
                granularity: showAllReadouts ? 'all' : granularity
            }
        },
        queryOptions: {
            enabled: !!meterID && !!concentratorID
        },
        errorNotification: (error) => {
            if (error?.statusCode === 404) {
                return false; // suppress toast notifications for 404
            }
            return;
        }
    });



    // Auto-determine granularity based on time range
    const getAutoGranularity = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs): 'day' | 'week' | 'month' => {
        const diffDays = endDate.diff(startDate, 'days');
        // return 'day';
        if (diffDays <= 90) {
            return 'day';
        } else if (diffDays <= 365) {
            return 'week';
        } else {
            return 'month';
        }
    };

    // Zoom by selection handler - updates date range instead of domain
    const applyZoomRange = (startMs: number, endMs: number) => {
        const startDate = dayjs(startMs);
        const endDate = dayjs(endMs);
        setDateRange([startDate, endDate]);
        const newGranularity = getAutoGranularity(startDate, endDate);
        setGranularity(newGranularity);
    };

    const handleZoom = () => {
        let { refAreaLeft, refAreaRight } = zoomState;

        // Check for valid numeric selection
        if (refAreaLeft === refAreaRight || refAreaRight === '' || refAreaLeft === '') {
            setZoomState(prev => ({
                ...prev,
                refAreaLeft: '',
                refAreaRight: '',
            }));
            return;
        }

        // Convert to numbers
        const leftNum = Number(refAreaLeft);
        const rightNum = Number(refAreaRight);
        if (isNaN(leftNum) || isNaN(rightNum)) {
            setZoomState(prev => ({
                ...prev,
                refAreaLeft: '',
                refAreaRight: '',
            }));
            return;
        }

        applyZoomRange(Math.min(leftNum, rightNum), Math.max(leftNum, rightNum));

        // Clear zoom state
        setZoomState(prev => ({
            ...prev,
            refAreaLeft: '',
            refAreaRight: '',
        }));
    };

    // Store pre-zoom date range for zoom out
    // Store initial range from range picker for zoom out
    const [initialDateRange, setInitialDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(30, 'days'),
        dayjs()
    ]);

    // Zoom out handler - restores initial picker range
    const handleZoomOut = () => {
        setDateRange(initialDateRange);
        const newGranularity = getAutoGranularity(initialDateRange[0], initialDateRange[1]);
        setGranularity(newGranularity);
    };


    // Format chart data: add numeric timestamp for X axis
    const formatChartData = (data: ChartDataPoint[]): any[] => {
        return data.map((point) => ({
            ...point,
            timestamp: dayjs(point.timestamp).format('YYYY-MM-DD HH:mm'),
            timestampNum: dayjs(point.timestamp).valueOf(),
        }));
    };

    const meter = chartData?.data?.meter;
    const data = chartData?.data?.data || [];
    const queryDetails = chartData?.data?.query;
    const formattedData = useMemo(() => formatChartData(data), [data]);

    // Clear selection state when new data loads
    useEffect(() => {
        if (formattedData.length > 0) {
            setZoomState({
                refAreaLeft: '',
                refAreaRight: '',
            });
        }
    }, [formattedData]);

    // Render error state
    if (!meterID) {
        return (
            <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
                <Alert
                    message="Error"
                    description="Invalid meter ID. Please check the URL."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (error?.statusCode === 404) {
        return (
            <div style={{ padding: 24 }}>
                <ErrorComponent />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
                <Alert
                    message="Error Loading Chart Data"
                    description={error.message || "Failed to load meter chart data"}
                    type="error"
                    showIcon
                    action={
                        <Button size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                />
            </div>
        );
    }




    return (
        <Space direction="vertical" size="large" style={{ width: '100%', padding: 24 }}>
            {/* Breadcrumb */}
            {/* <Breadcrumb
                items={[
                    {
                        href: '/concentrators',
                        title: <HomeOutlined />
                    },
                    {
                        href: '#',
                        title: 'Concentrators',
                        onClick: () => navigate('/concentrators')
                    },
                    {
                        href: '#',
                        title: concentratorID ? `Concentrator ${concentratorID}` : 'Concentrator',
                        onClick: () => concentratorID && navigate(`/concentrators/${concentratorID}`)
                    },
                    {
                        href: '#',
                        title: 'Meters',
                        onClick: () => concentratorID && navigate(`/concentrators/${concentratorID}/meters`)
                    },
                    {
                        title: (
                            <Space>
                                <LineChartOutlined />
                                Chart
                            </Space>
                        )
                    }
                ]}
            /> */}
            {/* Page Header */}
            {isLoading ? (
                <Skeleton.Input active style={{ width: 400, height: 32 }} />
            ) : (
                <div>
                    <Title level={2}>
                        {meter?.name || `Meter ${meterID}`}
                    </Title>
                    <Text type="secondary">
                        {meter?.media && (
                            <span style={{ textTransform: 'capitalize' }}>
                                {meter.media}
                            </span>
                        )}
                        {meter?.unit && ` (${meter.unit})`}
                        {meter?.uuid && (
                            <>
                                {' • UUID: '}
                                <Text code>{meter.uuid}</Text>
                            </>
                        )}
                    </Text>
                </div>
            )}
            {/* Chart Controls */}
            <Card size="small">
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={16}>
                        <Space>
                            <Text strong>Date Range:</Text>
                            <RangePicker
                                value={dateRange}
                                onChange={(dates) => {
                                    if (dates && dates[0] && dates[1]) {
                                        const newRange: [dayjs.Dayjs, dayjs.Dayjs] = [dates[0], dates[1]];
                                        setDateRange(newRange);
                                        setInitialDateRange(newRange); // Only update on picker change
                                        // Auto-adjust granularity
                                        const newGranularity = getAutoGranularity(newRange[0], newRange[1]);
                                        setGranularity(newGranularity);
                                    }
                                }}
                                format="YYYY-MM-DD"
                                size="small"
                                presets={[
                                    {
                                        label: 'Last 7 Days',
                                        value: [dayjs().subtract(7, 'days'), dayjs()],
                                    },
                                    {
                                        label: 'Last 30 Days',
                                        value: [dayjs().subtract(30, 'days'), dayjs()],
                                    },
                                    {
                                        label: 'Last 3 Months',
                                        value: [dayjs().subtract(90, 'days'), dayjs()],
                                    },
                                    {
                                        label: 'Last Year',
                                        value: [dayjs().subtract(365, 'days'), dayjs()],
                                    },
                                ]}
                            />
                        </Space>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Space>
                            <Checkbox
                                checked={showAllReadouts}
                                onChange={(e) => setShowAllReadouts(e.target.checked)}
                                style={{ fontSize: 12 }}
                            >
                                Show all readouts
                            </Checkbox>

                            <Tooltip title="Zoom Out">
                                <Button
                                    onClick={handleZoomOut}
                                    disabled={dateRange[0].isSame(initialDateRange[0]) && dateRange[1].isSame(initialDateRange[1])}
                                    size="small"
                                >
                                    Zoom Out
                                </Button>
                            </Tooltip>

                            <Tooltip title="Refresh">
                                <Button
                                    icon={<ReloadOutlined />}
                                    onClick={() => refetch()}
                                    loading={isLoading}
                                    size="small"
                                />
                            </Tooltip>
                        </Space>
                    </Col>
                </Row>
            </Card>
            {/* Chart Container */}
            <Card>
                <div
                    style={{
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'crosshair',
                        userSelect: 'none'
                    }}
                >
                <Spin spinning={isLoading}>
                    {formattedData.length === 0 ? (
                        <Empty
                            description="No data available for the selected time range"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    ) : (
                        meter?.media === 'electricity' ? (
                            // Electricity: Separate charts
                            (<div style={{ height: 600 }}>
                                {/* Active Energy Chart */}
                                <div style={{ height: '55%', marginBottom: 20 }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Active Energy (kWh)</h4>
                                    <ZoomableMultiLineChart
                                        data={formattedData}
                                        xKey="timestampNum"
                                        lines={[
                                            { key: 'active_value', color: '#52c41a', name: 'Active Energy' },
                                        ]}
                                        onRangeSelect={applyZoomRange}
                                    />
                                </div>
                                {/* Reactive Energy Chart */}
                                <div style={{ height: '40%' }}>
                                    <h4 style={{ margin: '0 0 10px 0', fontSize: 14 }}>Reactive Energy (kVArh)</h4>
                                    <ZoomableMultiLineChart
                                        data={formattedData}
                                        xKey="timestampNum"
                                        lines={[
                                            { key: 'reactive_value', color: '#1890ff', name: 'Reactive Energy' },
                                        ]}
                                        onRangeSelect={applyZoomRange}
                                    />
                                </div>
                            </div>)
                                ) : meter?.media === 'heat' ? (
                                    // Heat: Single line with dual Y-axis scales using ComposedChart
                                    (<div style={{ height: 500 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart
                                                data={formattedData}
                                                onMouseDown={(e) => {
                                                        setZoomState(prev => ({ ...prev, refAreaLeft: e?.activeLabel || '' }));
                                                }}
                                                onMouseMove={(e) => {
                                                    if (zoomState.refAreaLeft) {
                                                            setZoomState(prev => ({ ...prev, refAreaRight: e?.activeLabel || '' }));
                                                    }
                                                }}
                                                onMouseUp={handleZoom}
                                                margin={{ top: 20, right: 80, bottom: 80, left: 80 }}
                                            >
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                                                                        <XAxis
                                                                                            dataKey="timestampNum"
                                                                                            type="number"
                                                                                            domain={[dateRange[0].valueOf(), dateRange[1].valueOf()]}
                                                                                            tick={{ fontSize: 10 }}
                                                                                            tickFormatter={(value) => {
                                                                                                if (granularity === 'day') {
                                                                                                    return dayjs(value).format('MM/DD HH:mm');
                                                                                                } else if (granularity === 'week') {
                                                                                                    return dayjs(value).format('MM/DD');
                                                                                                } else {
                                                                                                    return dayjs(value).format('MMM YY');
                                                                                                }
                                                                                            }}
                                                                                            angle={-45}
                                                                                            textAnchor="end"
                                                                                            height={70}
                                                                                            tickLine={false}
                                                                                            axisLine={false}
                                                                                        />
                                                <YAxis
                                                    yAxisId="left"
                                                    tick={{ fontSize: 10 }}
                                                    tickCount={6}
                                                    width={60}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    domain={[(dataMin: number) => dataMin * 0.97, (dataMax: number) => dataMax * 1.03]}
                                                    tickFormatter={(value) => Number(value).toFixed(2)}
                                                    label={{ value: 'kWh', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                                                />
                                                <YAxis
                                                    yAxisId="right"
                                                    orientation="right"
                                                    tick={{ fontSize: 10 }}
                                                    tickCount={6}
                                                    width={60}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    domain={[(dataMin: number) => dataMin * 0.97, (dataMax: number) => dataMax * 1.03]}
                                                    label={{ value: 'GJ', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                                                    tickFormatter={(value) => (value / 277.778).toFixed(3)}
                                                />
                                                <RechartsTooltip
                                                    formatter={(value: any) => {
                                                        const kwhValue = Number(value).toFixed(2);
                                                        const gjValue = (Number(value) / 277.778).toFixed(3);
                                                        return [
                                                            <div key="energy-values">
                                                                <div>{kwhValue} kWh</div>
                                                                <div>{gjValue} GJ</div>
                                                            </div>,
                                                            'Energy'
                                                        ];
                                                    }}
                                                    labelFormatter={(value) => `Time: ${dayjs(value).format('DD.MM.YYYY HH:mm')}`}
                                                />
                                                <Legend />
                                                <Line
                                                    yAxisId="left"
                                                    type="linear"
                                                    dataKey="value_kwh"
                                                    stroke="#fa8c16"
                                                    strokeWidth={3}
                                                    name="Energy"
                                                    connectNulls={false}
                                                    dot={{ r: 2 }}
                                                    animationDuration={300}
                                                />
                                                <Line
                                                    yAxisId="right"
                                                    type="linear"
                                                    dataKey="value_kwh"
                                                    stroke="transparent"
                                                    strokeWidth={0}
                                                    connectNulls={false}
                                                    dot={false}
                                                    animationDuration={0}
                                                    legendType="none"
                                                />
                                                {zoomState.refAreaLeft && zoomState.refAreaRight && (
                                                    <ReferenceArea
                                                        yAxisId="left"
                                                        x1={zoomState.refAreaLeft}
                                                        x2={zoomState.refAreaRight}
                                                        strokeOpacity={0.3}
                                                        fill="#ffeaa7"
                                                        fillOpacity={0.3}
                                                    />
                                                )}
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>)
                                ) : (
                                    // Water: Single chart
                                    (<div style={{ height: 500 }}>
                                        <ZoomableMultiLineChart
                                            data={formattedData}
                                            xKey="timestampNum"
                                            lines={[
                                                { key: 'value', color: '#1890ff', name: 'Water Consumption (m³)' },
                                            ]}
                                            onRangeSelect={applyZoomRange}
                                        />
                                    </div>)
                                )
                    )}
                </Spin>
                </div>
            </Card>
            {/* Chart Info */}
            {queryDetails && (
                <Card size="small">
                    <Text type="secondary">
                        Showing {queryDetails.total_points} data points
                        from {dayjs(queryDetails.start_date).format('YYYY-MM-DD')}
                        {' '}to {dayjs(queryDetails.end_date).format('YYYY-MM-DD')}
                        {' • '}
                        Granularity: {queryDetails.granularity}
                        {' • '}
                        Click and drag to select range
                    </Text>
                </Card>
            )}
        </Space>
    );
};
